import axios from '@/libs/axios'

class PensumsService {
  /**
   * Obtener todos los pensums
   * @returns {Promise<Array>} - Lista de pensums
   */
  async getAll() {
    try {
      const response = await axios.get('/pensums')
      return response.data
    } catch (error) {
      console.error('Error al obtener pensums:', error)
      throw error
    }
  }

  /**
   * Obtener un pensum por ID
   * @param {number} id - ID del pensum
   * @returns {Promise<Object>} - Datos del pensum
   */
  async getById(id) {
    try {
      const response = await axios.get(`/pensums/${id}`)
      return response.data
    } catch (error) {
      console.error('Error al obtener pensum:', error)
      throw error
    }
  }

  /**
   * Obtener un pensum completo con estructura de semestres
   * @param {number} id - ID del pensum
   * @returns {Promise<Object>} - Pensum completo con semestres y cursos
   */
  async getCompleto(id) {
    try {
      const response = await axios.get(`/pensums/${id}/completo`)
      return response.data
    } catch (error) {
      console.error('Error al obtener pensum completo:', error)
      throw error
    }
  }

  /**
   * Obtener cursos asignados a un pensum
   * @param {number} id - ID del pensum
   * @returns {Promise<Array>} - Lista de cursos del pensum
   */
  async getCursos(id) {
    try {
      const response = await axios.get(`/pensums/${id}/cursos`)
      return response.data
    } catch (error) {
      console.error('Error al obtener cursos del pensum:', error)
      throw error
    }
  }

  /**
   * Obtener pensums por carrera
   * @param {number} carreraId - ID de la carrera
   * @returns {Promise<Array>} - Lista de pensums de la carrera
   */
  async getByCarrera(carreraId) {
    try {
      const response = await axios.get(`/pensums/carrera/${carreraId}`)
      return response.data
    } catch (error) {
      console.error('Error al obtener pensums por carrera:', error)
      throw error
    }
  }

  /**
   * Crear un nuevo pensum
   * @param {Object} pensumData - Datos del pensum
   * @returns {Promise<Object>} - Pensum creado
   */
  async create(pensumData) {
    try {
      const response = await axios.post('/pensums', pensumData)
      return response.data
    } catch (error) {
      console.error('Error al crear pensum:', error)
      throw error
    }
  }

  /**
   * Actualizar un pensum
   * @param {number} id - ID del pensum
   * @param {Object} pensumData - Datos actualizados
   * @returns {Promise<Object>} - Pensum actualizado
   */
  async update(id, pensumData) {
    try {
      const response = await axios.put(`/pensums/${id}`, pensumData)
      return response.data
    } catch (error) {
      console.error('Error al actualizar pensum:', error)
      throw error
    }
  }

  /**
   * Eliminar un pensum
   * @param {number} id - ID del pensum
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      await axios.delete(`/pensums/${id}`)
    } catch (error) {
      console.error('Error al eliminar pensum:', error)
      throw error
    }
  }

  /**
   * Guardar estructura de pensum (semestres y cursos)
   * @param {number} pensumId - ID del pensum
   * @param {Object} estructuraData - Estructura del pensum
   * @returns {Promise<Object>} - Estructura guardada
   */
  async saveEstructura(pensumId, estructuraData) {
    try {
      const response = await axios.post(`/pensums/${pensumId}/estructura`, estructuraData)
      return response.data
    } catch (error) {
      console.error('Error al guardar estructura del pensum:', error)
      throw error
    }
  }

  /**
   * Asignar curso a un pensum
   * @param {number} pensumId - ID del pensum
   * @param {number} cursoId - ID del curso
   * @param {number} semestre - Número del semestre
   * @param {number} orden - Orden dentro del semestre (opcional)
   * @returns {Promise<Object>} - Asignación creada
   */
  async asignarCurso(pensumId, cursoId, semestre, orden = null) {
    try {
      const response = await axios.post(`/pensums/${pensumId}/cursos`, {
        cursoId,
        semestre,
        orden
      })
      return response.data
    } catch (error) {
      console.error('Error al asignar curso al pensum:', error)
      throw error
    }
  }

  /**
   * Remover curso de un pensum
   * @param {number} pensumId - ID del pensum
   * @param {number} cursoId - ID del curso
   * @returns {Promise<void>}
   */
  async removerCurso(pensumId, cursoId) {
    try {
      await axios.delete(`/pensums/${pensumId}/cursos/${cursoId}`)
    } catch (error) {
      console.error('Error al remover curso del pensum:', error)
      throw error
    }
  }

  /**
   * Actualizar semestre de un curso en el pensum
   * @param {number} pensumId - ID del pensum
   * @param {number} cursoId - ID del curso
   * @param {number} semestre - Nuevo semestre
   * @param {number} orden - Nuevo orden (opcional)
   * @returns {Promise<Object>} - Asignación actualizada
   */
  async actualizarCursoEnPensum(pensumId, cursoId, semestre, orden = null) {
    try {
      const response = await axios.put(`/pensums/${pensumId}/cursos/${cursoId}`, {
        semestre,
        orden
      })
      return response.data
    } catch (error) {
      console.error('Error al actualizar curso en pensum:', error)
      throw error
    }
  }

  /**
   * Guardar estructura completa del pensum con asignaciones de cursos
   * @param {number} pensumId - ID del pensum
   * @param {Array} semesters - Array de semestres con cursos
   * @returns {Promise<Object>} - Resultado de la operación
   */
  async guardarEstructuraCompleta(pensumId, semesters) {
    try {
      // Preparar datos: Aplanar la estructura de semestres
      const asignaciones = []
      semesters.forEach((semester, index) => {
        semester.courses.forEach((course, orden) => {
          asignaciones.push({
            cursoId: course.cursoId,
            semestre: index + 1,
            orden: orden + 1
          })
        })
      })

      const response = await axios.post(`/pensums/${pensumId}/estructura-completa`, {
        asignaciones
      })
      return response.data
    } catch (error) {
      console.error('Error al guardar estructura completa:', error)
      throw error
    }
  }
}

export default new PensumsService()
